<template>
  <div class="image-upload-container" :style="{ width: containerWidth }">
    <v-layout row align-center>
      <v-flex :class="{ 'xs8': selectedImage, 'xs12': !selectedImage }">
        <v-autocomplete
          v-model="selectedImage"
          :items="imageOptions"
          :label="label"
          :placeholder="placeholder"
          item-text="name"
          item-value="downloadURL"
          clearable
          :loading="loading || isUploading"
          :disabled="disabled"
          @change="handleSelectionChange"
          @click:clear="handleClear"
          return-object
          hide-details
          :rules="rules"
        >
          <template slot="selection" slot-scope="data">
            <v-chip class="ma-1" v-if="data.item">
              <v-avatar left>
                <img :src="data.item.thumbnail || data.item.base64" :alt="data.item.name" />
              </v-avatar>
              {{ data.item.name }}
            </v-chip>
          </template>
          <template slot="item" slot-scope="data">
            <template v-if="typeof data.item !== 'object'">
              <v-list-tile-content v-text="data.item"></v-list-tile-content>
            </template>
            <template v-else>
              <v-list-tile-avatar>
                <img :src="data.item.thumbnail || data.item.base64" :alt="data.item.name" />
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                <v-list-tile-sub-title v-html="formatFileSize(data.item.size)"></v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-btn
                  icon
                  small
                  :loading="deletingItemId === data.item.id"
                  :disabled="disabled"
                  @click.stop="handleRemoveItem(data.item)"
                  class="remove-btn"
                >
                  <v-icon small color="error">delete</v-icon>
                </v-btn>
              </v-list-tile-action>
            </template>
          </template>
          <template slot="append-outer">
            <UploadFileWidget
              ref="uploadWidget"
              type="letterheads"
              :acceptTypes="acceptedTypes"
              :sizeLimit="maxFileSize"
              :small="true"
              :icon="true"
              iconType="add_photo_alternate"
              :disabled="disabled"
              @getUploadingFileData="handleFileUpload"
              @error="handleUploadError"
              @filesStatus="handleFilesStatus"
            />
          </template>
        </v-autocomplete>
      </v-flex>

      <v-flex xs4 v-if="selectedImage" class="pl-2">
        <v-btn
          color="primary"
          outline
          block
          small
          @click="showImageDialog = true"
          :disabled="disabled"
          class="view-btn"
        >
          <v-icon left>visibility</v-icon>
          {{ $t('view') }}
        </v-btn>
      </v-flex>
    </v-layout>

    <v-dialog
      v-model="showImageDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      content-class="image-preview-dialog"
    >
      <div class="image-overlay" @click="showImageDialog = false">
          <v-layout column align-center justify-center class="image-container" @click.stop>
            <v-flex xs12 class="image-toolbar">
              <v-card color="rgba(0,0,0,0.7)" class="pa-3">
                <v-layout align-center>
                  <v-flex class="white--text">
                    <h3 class="ma-0">{{ selectedImage ? selectedImage.name : '' }}</h3>
                  </v-flex>
                  <v-spacer></v-spacer>
                  <v-btn icon dark @click="showImageDialog = false" class="ma-0">
                    <v-icon color="white" large>close</v-icon>
                  </v-btn>
                </v-layout>
              </v-card>
            </v-flex>

            <v-flex xs12 class="image-content">
              <div class="image-wrapper">
                <img
                  v-if="selectedImage"
                  :src="selectedImage.base64 || selectedImage.thumbnail"
                  :alt="selectedImage.name"
                  class="preview-image"
                />
                <v-layout
                  v-else
                  fill-height
                  align-center
                  justify-center
                >
                  <v-progress-circular indeterminate color="white" size="70"></v-progress-circular>
                </v-layout>
              </div>
            </v-flex>

            <v-flex xs12 class="image-footer">
              <v-card color="rgba(0,0,0,0.7)" class="pa-3">
                <v-layout align-center justify-space-between>
                  <v-flex class="white--text">
                    <div>
                      <strong>{{ $t('fileSize') }}:</strong> {{ selectedImage ? formatFileSize(selectedImage.size) : '' }} |
                      <strong>{{ $t('fileType') }}:</strong> {{ selectedImage ? selectedImage.type : '' }}
                    </div>
                    <div v-if="selectedImage && selectedImage.createdAt">
                      <strong>{{ $t('uploadTime') }}:</strong>
                      {{ new Date(selectedImage.createdAt.toDate ? selectedImage.createdAt.toDate() : selectedImage.createdAt).toLocaleString() }}
                    </div>
                  </v-flex>
                </v-layout>
              </v-card>
            </v-flex>
          </v-layout>
        </div>
    </v-dialog>
  </div>
</template>

<script>
import UploadFileWidget from './UploadFileWidget.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'ETDSelector',
  components: {
    UploadFileWidget
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: 'Select Image'
    },
    placeholder: {
      type: String,
      default: 'Select or upload an image'
    },
    maxFileSize: {
      type: Number,
      default: 5242880 // 5MB
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showImageDetail: {
      type: Boolean,
      default: true
    },
    containerWidth: {
      type: String,
      default: '100%'
    },
    rules: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      imageOptions: [],
      // selectedImage: null,
      loading: false,
      uploading: false,
      isUploading: false,
      acceptedTypes: ['image/png'],
      showImageDialog: false,
      deletingItemId: null
    }
  },
  computed: {
    selectedImage: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    ...mapGetters(['tenant', 'letterheads'])
  },
  watch: {
    value: {
      handler (newVal) {
        // this.selectedImage = newVal
        this.$nextTick(() => {
          this.validateSelectedImage()
        })
      },
      immediate: true
    },
    letterheads: {
      handler (newVal) {
        this.imageOptions = newVal || []
        this.$nextTick(() => {
          this.validateSelectedImage()
        })
      },
      immediate: true
    }
  },
  async mounted () {
    await this.loadImagesFromDatabase()
  },
  methods: {
    async loadImagesFromDatabase () {
      this.loading = true
      try {
        await this.$store.dispatch('loadLetterheads')
        this.updateImageOptions()
        if (!this.value) {
          this.$emit('input', this.imageOptions[0])
        }
      } catch (error) {
        console.error('Error loading images from database:', error)
        this.$store.dispatch('showToast', {
          info: this.$t('failedToLoadImages'),
          level: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    updateImageOptions () {
      this.imageOptions = this.$store.getters.letterheads
    },

    async handleFileUpload (fileData) {
      this.uploading = true
      this.isUploading = true
      try {
        const file = fileData.file

        const base64 = await this.fileToBase64(file)

        const imageData = {
          name: file.name,
          size: file.size,
          type: file.type,
          base64: base64,
          downloadURL: fileData.metadata?.downloadURL,
          fullPath: fileData.metadata?.fullPath
        }

        const newImage = await this.$store.dispatch('addLetterhead', imageData)

        this.updateImageOptions()

        this.selectedImage = newImage
        this.updateValue()

        this.$emit('imageUploaded', newImage)
        this.$store.dispatch('showToast', {
          info: this.$t('uploadSuccess'),
          level: 'success'
        })
      } catch (error) {
        console.error('Error processing uploaded file:', error)
        this.$store.dispatch('showToast', {
          info: this.$t('failedToProcessImage'),
          level: 'error'
        })
      } finally {
        this.uploading = false
        this.isUploading = false
      }
    },

    handleUploadError (error) {
      console.error('Upload error:', error)
      this.isUploading = false
      this.$store.dispatch('showToast', {
        info: this.$t('uploadImageError'),
        level: 'error'
      })
    },
    handleFilesStatus (status) {
      console.log('Upload files status:', status)
      this.isUploading = status === 'done' || status === 'processing'
    },

    handleSelectionChange (selectedItem) {
      this.selectedImage = selectedItem
      console.log(selectedItem)
      this.updateValue()
    },

    handleClear () {
      this.selectedImage = null
      this.updateValue()
    },

    async removeImage () {
      if (this.selectedImage && confirm(this.$t('confirmDeleteImage'))) {
        try {
          await this.$store.dispatch('removeLetterhead', this.selectedImage.id)

          this.updateImageOptions()

          const deletedImageId = this.selectedImage.id
          this.selectedImage = null
          this.updateValue()

          this.$emit('imageDeleted', deletedImageId)
          this.$store.dispatch('showToast', {
            info: this.$t('imageDeleteSuccess'),
            level: 'success'
          })
        } catch (error) {
          console.error('Error deleting image:', error)
          this.$store.dispatch('showToast', {
            info: this.$t('failedToDeleteImage'),
            level: 'error'
          })
        }
      }
    },

    async handleRemoveItem (item) {
      if (confirm(this.$t('confirmDeleteImage'))) {
        this.deletingItemId = item.id
        try {
          await this.$store.dispatch('removeLetterhead', item.id)

          // If the deleted item is currently selected, clear selection
          if (this.selectedImage && this.selectedImage.id === item.id) {
            this.selectedImage = null
            this.updateValue()
          }

          this.updateImageOptions()

          this.$emit('imageDeleted', item.id)
          this.$store.dispatch('showToast', {
            info: this.$t('imageDeleteSuccess'),
            level: 'success'
          })
        } catch (error) {
          console.error('Error deleting image:', error)
          this.$store.dispatch('showToast', {
            info: this.$t('failedToDeleteImage'),
            level: 'error'
          })
        } finally {
          this.deletingItemId = null
        }
      }
    },
    updateValue () {
      this.$emit('input', this.selectedImage)
      this.$emit('change', this.selectedImage)
    },

    fileToBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    validateSelectedImage () {
      if (!this.selectedImage) return

      const exists = this.imageOptions.some(img => {
        if (this.selectedImage.downloadURL && img.downloadURL === this.selectedImage.downloadURL) {
          return true
        }
        return false
      })

      if (!exists) {
        this.selectedImage = null
        this.updateValue()
      }
    }
  }
}
</script>

<style scoped>
.image-upload-container {
  position: relative;
}

.v-select {
  position: relative;
}

.v-select .v-input__append-outer {
  margin-left: 8px;
  margin-top: 0;
  margin-bottom: 0;
  align-self: center;
}

.view-btn {
  min-height: 42px;
}

/* 确保autocomplete和按钮高度一致 */
.v-input__control {
  min-height: 42px;
}

/* 响应式布局调整 */
@media (max-width: 600px) {
  .image-upload-container .v-flex {
    margin-bottom: 8px;
  }
}

/* 遮罩层样式 */
.image-preview-dialog {
  background: transparent !important;
  box-shadow: none !important;
  overflow: hidden !important;
}

.image-overlay {
  background: rgba(0, 0, 0, 0.85);
  min-height: 100vh;
  width: 100vw;
  position: relative;
  cursor: pointer;
}

.image-container {
  height: 100vh;
  padding: 20px;
}

.image-toolbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

.image-content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: calc(100vh - 160px);
  overflow: hidden;
}

.image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90vw;
  height: 70vh;
  max-width: 90vw;
  max-height: 70vh;
  overflow: hidden;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  background-color: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.image-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
}

/* 图片过渡动画 */
.scale-transition-enter-active,
.scale-transition-leave-active {
  transition: all 0.3s ease;
}

.scale-transition-enter,
.scale-transition-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* 删除按钮样式 */
.remove-btn {
  margin: 0;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.remove-btn:hover {
  opacity: 1;
}

.v-list__tile:hover .remove-btn {
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/ETDSelector.vue